package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.safeSnapshots
import dev.gitlive.firebase.firestore.Direction
import com.evoapps.evoshift.data.dto.system.NotificationDto
import com.evoapps.evoshift.data.dto.auth.SensitiveGeneralDto
import com.evoapps.evoshift.data.dto.auth.SensitiveWorkplaceDto
import com.evoapps.evoshift.data.dto.auth.UserDto
import com.evoapps.evoshift.domain.model.Appearance
import com.evoapps.evoshift.domain.model.NotFoundException
import dev.gitlive.firebase.firestore.DocumentSnapshot
import com.evoapps.evoshift.data.mapper.EngagementMapper
import com.evoapps.evoshift.data.mapper.NotificationMapper
import com.evoapps.evoshift.data.mapper.SensitiveDataMapper
import com.evoapps.evoshift.data.mapper.UserMapper
import com.evoapps.evoshift.domain.model.Competence
import com.evoapps.evoshift.domain.model.Engagement
import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.Notification
import com.evoapps.evoshift.domain.model.SensitiveUserData
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.model.UserPermission
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.data.remote.AppFirestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.distinctUntilChanged
import com.evoapps.evoshift.data.remote.isPermissionDenied
import kotlinx.coroutines.flow.map
import com.evoapps.evoshift.domain.util.AppClock.now
import com.evoapps.evoshift.data.dto.auth.DeviceDto
import com.evoapps.evoshift.data.mapper.DeviceMapper
import com.evoapps.evoshift.domain.model.Device

class UserRepositoryImpl(
    private val firestore: AppFirestore,
    private val userMapper: UserMapper,
    private val engagementMapper: EngagementMapper,
    private val sensitiveDataMapper: SensitiveDataMapper,
    private val deviceMapper: DeviceMapper,
    private val notificationMapper: NotificationMapper
) : UserRepository {

    // --- ODCZYT PROFILU PUBLICZNEGO ---
    override suspend fun getUserProfile(uid: String): Result<User> {
        return try {
            val snapshot = firestore.collection("users").document(uid).get()
            if (snapshot.exists) {
                val userDto = snapshot.data<UserDto>()
                // Email przekazujemy jako null, bo w profilu publicznym go nie ma (jest w sensitive/general)
                // Jeśli użytkownik jest zalogowany, email weźmie z Auth (w AuthRepository),
                // a jeśli oglądamy cudzy profil, email ma być ukryty.
                Result.success(userMapper.toDomain(userDto, emailFromAuth = null))
            } else {
                Result.failure(Exception("User not found"))
            }
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    // --- ODCZYT DANYCH WRAŻLIWYCH (AGREGACJA) ---
    /**
     * The person's private documents read one by one: 'general' and one per workplace of their
     * engagements. firestore.rules decide per document (a financial manager sees the rates of
     * their own workplace only), so listing the subcollection is denied to anyone but the person;
     * documents the caller may not read are left out.
     */
    override suspend fun getSensitiveUserData(uid: String): Result<SensitiveUserData> {
        return try {
            val sensitive = firestore.collection("users").document(uid).collection("sensitive")
            val workplaceIds = firestore.collection("users").document(uid).get()
                .takeIf { it.exists }?.data<UserDto>()?.engagements?.keys.orEmpty()

            suspend fun <T> read(docId: String, parse: (DocumentSnapshot) -> T): T? =
                runCatching { sensitive.document(docId).get().takeIf { it.exists }?.let(parse) }.getOrNull()

            val generalDto = read("general") { it.data<SensitiveGeneralDto>() }
            val workplaceDtos = workplaceIds.mapNotNull { wp ->
                read(wp) { it.data<SensitiveWorkplaceDto>() }?.let { wp to it }
            }.toMap()
            if (generalDto == null && workplaceDtos.isEmpty()) {
                return Result.failure(NotFoundException("sensitive data of $uid"))
            }

            Result.success(
                sensitiveDataMapper.toDomain(
                    userId = uid,
                    generalDto = generalDto ?: SensitiveGeneralDto(),
                    workplaceDtos = workplaceDtos
                )
            )
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override fun observeFinancialEngagement(uid: String, workplaceId: String): Flow<FinancialEngagement?> =
        firestore.collection("users").document(uid).collection("sensitive").document(workplaceId).snapshots
            .map { snapshot ->
                if (!snapshot.exists) null
                else sensitiveDataMapper.toDomain(uid, SensitiveGeneralDto(), mapOf(workplaceId to snapshot.data<SensitiveWorkplaceDto>()))
                    .financialEngagements[workplaceId]
            }
            // A refusal (no right to read pay any more) is "not readable", as for a one-off read.
            .catch { e -> if (e.isPermissionDenied()) emit(null) else throw e }
            .distinctUntilChanged()

    // --- TWORZENIE PRACOWNIKA (SHADOW USER) ---
    override suspend fun createEmployee(
        user: User,
        sensitiveData: SensitiveUserData?
    ): Result<Unit> {
        return try {
            val batch = firestore.batch()

            // 1. Używamy ID przekazanego w obiekcie User (z UseCase)
            val newUserId = user.uid
            val userRef = firestore.collection("users").document(newUserId)

            // 2. Profil Publiczny (Shadow)
            // Konwertujemy domenowy User -> UserDto
            // Mapper UserMapper powinien obsłużyć mapę engagements
            val userDto = userMapper.toDto(user).copy(
                isShadow = true
            )
            batch.set(userRef, userDto)

            // An operational manager adds the person without pay or personal data (the rules
            // let only financial managers write them).
            if (sensitiveData == null) {
                batch.commit()
                return Result.success(Unit)
            }

            // 3. Dane Wrażliwe - "GENERAL"
            val sensitiveGeneralRef = userRef.collection("sensitive").document("general")
            val sensitiveGeneralDto = SensitiveGeneralDto(
                email = sensitiveData.email,
                phoneNumber = sensitiveData.phoneNumber,
                address = sensitiveData.address,
                bankAccountNumber = sensitiveData.bankAccountNumber,
                isShadow = true
            )
            batch.set(sensitiveGeneralRef, sensitiveGeneralDto)

            // 4. Dane Wrażliwe - FIRMOWE
            sensitiveData.financialEngagements.forEach { (workplaceId, financialData) ->
                val sensitiveWorkplaceRef = userRef.collection("sensitive").document(workplaceId)

                // Używamy mappera, żeby zamienić domenowy FinancialEngagement na DTO
                val sensitiveWorkplaceDto = sensitiveDataMapper.toWorkplaceDto(financialData)

                batch.set(sensitiveWorkplaceRef, sensitiveWorkplaceDto)
            }

            batch.commit()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    // --- AKTUALIZACJA PEŁNYCH DANYCH ---
    override suspend fun updateEmployeeDetailsFull(
        userId: String,
        workplaceId: String,
        workplaceName: String,
        displayName: String,
        email: String?,
        role: String,
        skills: List<String>,
        permission: UserPermission,
        isPrimary: Boolean,
        financial: FinancialEngagement,
        phoneNumber: String?,
        address: String?,
        bankAccountNumber: String?,
        includeSensitive: Boolean,
        competence: Competence
    ): Result<Unit> {
        return try {
            val batch = firestore.batch()
            val userRef = firestore.collection("users").document(userId)

            // 1. Update Public Profile (User)
            // Używamy składni "dot notation" do aktualizacji zagnieżdżonych pól w mapie.
            // Typed fields: a List inside a Map<String, Any?> does not encode on Kotlin/JS (web
            // app: "RangeError: Invalid array length" on every employee edit).
            batch.updateFields(userRef) {
                "displayName" to displayName
                // Email nie jest w profilu publicznym (sensitive/general).
                "engagements.$workplaceId.role" to role
                "engagements.$workplaceId.workplaceName" to workplaceName
                "engagements.$workplaceId.skills" to skills
                "engagements.$workplaceId.permission" to permission.name
                "engagements.$workplaceId.isPrimary" to isPrimary
                "engagements.$workplaceId.competence" to competence.name
                "engagements.$workplaceId.isActive" to true
            }

            // Without the right to pay and personal data the sensitive documents stay untouched.
            if (!includeSensitive) {
                batch.commit()
                return Result.success(Unit)
            }

            // 2. Update Sensitive General (Email, Phone, Address)
            val sensitiveGeneralRef = userRef.collection("sensitive").document("general")
            // Tutaj update może rzucić błąd, jeśli dokument nie istnieje (np. stare dane).
            // Bezpieczniej użyć set z merge=true, ale w Firestore SDK batch.set(..., merge=true)
            // lub po prostu update, zakładając spójność danych.
            // filtrujemy null, jeśli chcemy zachować stare dane, lub przesyłamy null, żeby wyczyścić.
            // Tutaj zakładam, że nadpisujemy:
            batch.updateFields(sensitiveGeneralRef) {
                "email" to email
                "phoneNumber" to phoneNumber
                "address" to address
                "bankAccountNumber" to bankAccountNumber
            }

            // 3. Update Sensitive Workplace (Financials)
            val sensitiveWorkplaceRef = userRef.collection("sensitive").document(workplaceId)
            val workplaceDto = sensitiveDataMapper.toWorkplaceDto(financial)
            // Tutaj używamy SET, bo nadpisujemy całą strukturę finansową dla tej firmy
            batch.set(sensitiveWorkplaceRef, workplaceDto)

            batch.commit()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    // --- POZOSTAŁE METODY (BEZ WIĘKSZYCH ZMIAN) ---

    override suspend fun updateUserProfile(user: User): Result<Unit> {
        return try {
            // Field by field, as in terminateEngagement (mixed value types). Not the photo: it has
            // its own upload flow (ProfilePhotoRepository, SH-94), which a name edit never undoes.
            firestore.collection("users").document(user.uid).updateFields {
                "displayName" to user.displayName
                "isSetupComplete" to user.isSetupComplete
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateLanguage(uid: String, language: String, preference: String?): Result<Unit> {
        return try {
            firestore.collection("users").document(uid).updateFields {
                "language" to language
                "languagePreference" to preference
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateAppearance(uid: String, appearance: Appearance): Result<Unit> {
        return try {
            firestore.collection("users").document(uid).updateFields {
                "appearance.base" to appearance.base.name
                "appearance.accent" to appearance.accent.name
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun addEngagement(userId: String, engagement: Engagement): Result<Unit> {
        return try {
            val engagementDto = engagementMapper.toDto(engagement)
            // Dot path: only this workplace's engagement is replaced, the others stay.
            firestore.collection("users").document(userId).updateFields {
                "engagements.${engagement.workplaceId}" to engagementDto
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun addEngagementWithPayTerms(
        userId: String,
        engagement: Engagement,
        financial: FinancialEngagement?
    ): Result<Unit> {
        return try {
            val batch = firestore.batch()
            val userRef = firestore.collection("users").document(userId)
            // Dot path, as addEngagement: the person's other engagements stay untouched.
            batch.updateFields(userRef) {
                "engagements.${engagement.workplaceId}" to engagementMapper.toDto(engagement)
            }
            // The rules read the engagement as this batch leaves it (getAfter), so both go together.
            if (financial != null) {
                batch.set(
                    userRef.collection("sensitive").document(engagement.workplaceId),
                    sensitiveDataMapper.toWorkplaceDto(financial)
                )
            }
            batch.commit()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun terminateEngagement(userId: String, workplaceId: String): Result<Unit> {
        return try {
            val today = now().toString().substring(0, 10)
            // Field by field: a mapOf(false, "...") is a Map<String, Comparable<*>>, which the
            // Firebase encoder cannot serialize (no serializer for the Comparable interface).
            firestore.collection("users").document(userId).updateFields {
                "engagements.${workplaceId}.isActive" to false
                "engagements.${workplaceId}.endDate" to today
            }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getAssignableEmployees(workplaceId: String): Result<List<User>> {
        return try {
            // Everyone with an engagement here (every engagement has a permission), then the active
            // ones. Not `isActive == true` in the query: profiles from before the field existed have
            // none and count as active, as in firestore.rules (SH-41).
            val querySnapshot = firestore.collection("users")
                .where { "engagements.$workplaceId.permission" inArray ENGAGEMENT_PERMISSIONS }
                .get()

            // SH-235: one profile that does not read (an engagement elsewhere written in another
            // shape) is left out and logged, instead of failing the whole team: the screens named
            // nobody then.
            val users = querySnapshot.documents
                .mapNotNull { doc ->
                    runCatching { userMapper.toDomain(doc.data<UserDto>(), emailFromAuth = null) }
                        .onFailure { println("SH-235: profile ${doc.id} of $workplaceId's team not read: ${it.message}") }
                        .getOrNull()
                }
                .filter { it.engagements[workplaceId]?.isActive == true }
            Result.success(users)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun saveDeviceToken(uid: String, device: Device): Result<Unit> {
        return try {
            val dto = deviceMapper.toDto(device)
            firestore.collection("users").document(uid)
                .collection("devices").document(device.token).set(dto)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun removeDeviceToken(uid: String, token: String): Result<Unit> {
        return try {
            firestore.collection("users").document(uid)
                .collection("devices").document(token).delete()
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun getUserDevices(uid: String): Result<List<Device>> {
        return try {
            val snapshot = firestore.collection("users").document(uid)
                .collection("devices").get()
            val devices = snapshot.documents.map { 
                deviceMapper.toDomain(it.data<DeviceDto>()) 
            }
            Result.success(devices)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun updateCachedWorkplaceName(
        userId: String,
        workplaceId: String,
        newName: String
    ): Result<Unit> {
        return try {
            firestore.collection("users").document(userId)
                .updateFields { "engagements.$workplaceId.workplaceName" to newName }
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override suspend fun sendNotification(notification: Notification): Result<Unit> {
        return try {
            val dto = notificationMapper.toDto(notification)
            firestore.collection("users").document(notification.userId)
                .collection("notifications").add(dto)
            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }

    override fun observeNotifications(userId: String, limit: Int): Flow<List<Notification>> =
        firestore.collection("users").document(userId)
            .collection("notifications")
            .orderBy("createdAt", Direction.DESCENDING)
            .limit(limit)
            .safeSnapshots
            .map { snapshot ->
                // The document id is the notification id (add() does not store it in the document).
                snapshot.documents.map { notificationMapper.toDomain(it.data<NotificationDto>(), userId).copy(id = it.id) }
            }
            .distinctUntilChanged()

    override suspend fun markNotificationsRead(userId: String, notificationIds: Collection<String>): Result<Unit> = runCatching {
        if (notificationIds.isEmpty()) return@runCatching
        val batch = firestore.batch()
        val collection = firestore.collection("users").document(userId).collection("notifications")
        notificationIds.forEach { id -> batch.updateFields(collection.document(id)) { "isRead" to true } }
        batch.commit()
    }

    override fun observeUnreadNotificationsCount(userId: String): Flow<Int> {
        return firestore.collection("users").document(userId)
            .collection("notifications")
            .where { "isRead" equalTo false }
            .safeSnapshots
            .map { it.documents.size }
            .distinctUntilChanged()
    }
}

/** Every permission an engagement may hold, incl. the older MANAGER_OPERATIONAL (read as MANAGER by the rules). */
private val ENGAGEMENT_PERMISSIONS: List<String> = UserPermission.entries.map { it.name } + "MANAGER_OPERATIONAL"
